import { Buttons, MUIcon } from '@platform/shared/ui';
import { Helpers } from '@platform/utils';
import React, { useEffect, useState } from 'react';
import { RuleGroupType } from 'react-querybuilder';
import { useDataset } from '../../../hooks';
import DownloadCsvButton from '../../DownloadCsvButton';
import { Loader } from '../../shared';
import DatasetTiles from '../DatasetTiles';
import DatasetHeader from './DatasetHeader';
import DatasetTable from './DatasetTable';
import FilterableVariablesPanel from './FilterableVariablesPanel';
import DatasetFilter from './Filtering';
import { ModeType } from './index';
import MoreDatasetOptionsButton from './MoreDatasetOptionsButton';

interface Props {
  datasetId: string;
  mode: ModeType;
}

enum ACTION {
  DATASET_FILTER,
  COLUMN_META,
}

const DEFAULT_FILTER = { combinator: 'and', rules: [] };

const Dataset: React.FC<Props> = ({ datasetId, mode }) => {
  const [currentAction, setCurrentAction] = useState<ACTION | undefined>(undefined);
  const [currentDataFilter, setCurrentDataFilter] = useState<RuleGroupType>(DEFAULT_FILTER);
  const [dataSize, setDataSize] = useState<[number, number]>([0, 0]);
  const datasetQuery = useDataset(datasetId);

  useEffect(() => {
    setCurrentAction(undefined);
    setCurrentDataFilter(DEFAULT_FILTER);
  }, [datasetId]);

  if (datasetQuery.isLoading || datasetQuery.isIdle) return <Loader>Loading dataset</Loader>;
  if (datasetQuery.isError) return <>Something went wrong...</>;

  const handleDataFilterUpdate = (newFilter: RuleGroupType) => setCurrentDataFilter(newFilter);

  const dataset = datasetQuery.data;
  const countOfValidFilters = currentDataFilter.rules.filter((rule) => Helpers.checkIfValidRule(rule, null)).length;
  const filterButtonLabel = countOfValidFilters ? `Filter (${countOfValidFilters})` : 'Filter';

  return (
    <div className="flex h-full w-full gap-2">
      <div className="flex min-w-0 grow flex-col">
        <DatasetHeader
          dataset={dataset}
          mode={mode}
          actions={
            <>
              <DownloadCsvButton datasetId={datasetId} hasFilters={countOfValidFilters > 0} />
              {currentAction == null && (
                <Buttons.Secondary
                  icon={<MUIcon name="tune" />}
                  className="rounded-full px-4"
                  onClick={() => setCurrentAction(ACTION.DATASET_FILTER)}
                >
                  {filterButtonLabel}
                </Buttons.Secondary>
              )}
              <MoreDatasetOptionsButton
                dataset={dataset}
                onShowFilterableVars={() => setCurrentAction(ACTION.COLUMN_META)}
              />
            </>
          }
        />
        {mode === 'map' ? (
          <DatasetTiles dataset={dataset} filter={currentDataFilter} />
        ) : (
          <DatasetTable
            datasetId={datasetId}
            filter={currentDataFilter}
            onDataSizeChange={(r, c) => setDataSize([r, c])}
          />
        )}
      </div>
      <div className="flex shrink-0">
        {currentAction === ACTION.DATASET_FILTER && (
          <DatasetFilter
            dataset={dataset}
            filter={currentDataFilter}
            disabled={dataSize[0] * dataSize[1] <= 0}
            onCloseRequest={() => setCurrentAction(undefined)}
            onChange={handleDataFilterUpdate}
          />
        )}
        {currentAction === ACTION.COLUMN_META && (
          <div className="flex shrink-0">
            <FilterableVariablesPanel dataset={dataset} onCloseRequest={() => setCurrentAction(undefined)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dataset;
